import EditIcon from '@components/icons/Edit';
import SaveIcon from '@components/icons/SaveIcon';
import CardWrapper from '@components/UI/CardWrapper';
import { useBitrix } from '@context/useBitrixUser/useBitrix';
import { zodResolver } from '@hookform/resolvers/zod';
import { Popper } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RedoIcon from '@mui/icons-material/Redo';
import Autocomplete from '@mui/material/Autocomplete';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { companyProfileService } from '@services/requests/companyProfileService';
import { ItemSelector } from '@services/requests/companyProfileService/interface';
import { RepresentativeData } from '@services/requests/companyProfileService/types/representativesData';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ChangeNotification } from '../../ChangeNotification';
import { representativesFormSchema } from './schemas';
import { RepresentativesItemStyledContainer } from './StyledComponents';
import { RepresentativesFormFields } from './types';

interface RepresentativesItemProps {
  representativesItem: RepresentativesFormFields;
  index: number;
  representativeData: RepresentativeData;
  totalCount: number;
  onRemoveRepresentativesItem: (
    representativeIndex: number,
    id: string,
    onError: (errors: string[]) => void
  ) => void;
}

const CustomPopper = (props: any) => {
  return (
    <Popper
      {...props}
      placement="bottom"
      style={{ width: props.anchorEl.clientWidth, minWidth: 'fit-content' }}
    />
  );
};

function RepresentativesItem({
  representativesItem,
  index,
  representativeData,
  totalCount,
  onRemoveRepresentativesItem,
}: RepresentativesItemProps) {
  const [isChangeNotificationOpen, setIsChangeNotificationOpen] = useState<boolean>(false);

  const { useBitrixUser } = useBitrix();
  const [{ bitrixUser }] = useBitrixUser().useState();

  const form = useForm<RepresentativesFormFields>({
    resolver: zodResolver(representativesFormSchema),
    defaultValues: representativesItem,
  });

  const {
    handleSubmit,
    reset,
    control,
    watch,
    getValues,
    setError,
    formState: { errors },
  } = form;

  const { fields } = Object.values(representativeData.users)[0];

  const id = watch('id');

  const isNewRepresentative = id.includes('new-representative');

  const [isEditMode, setIsEditMode] = useState<boolean>(isNewRepresentative);

  const changeEditModeHandler = (value: boolean) => {
    setIsEditMode(value);
  };

  const submitRepresentativesFormHandler = async (data: RepresentativesFormFields) => {
    const result = await companyProfileService().SaveRepresentative(bitrixUser.id, data);

    if (!result.success) {
      if (!!result.errors && result.errors.length >= 1) {
        setError('root', { message: result.errors[0] });
      }
      return;
    }

    const newDefaultValues = { ...getValues() };
    if (isNewRepresentative) {
      newDefaultValues.id = result.user.id.toString();
    }

    setIsChangeNotificationOpen(true);
    setIsEditMode(false);
    reset(newDefaultValues);
  };

  const cancelEditingHandler = () => {
    reset();
    setIsEditMode(false);
  };

  return (
    <>
      <RepresentativesItemStyledContainer>
        <form onSubmit={handleSubmit(submitRepresentativesFormHandler)}>
          <CardWrapper sx={{ padding: '25px 40px' }} className="card">
            <div className="fields">
              <Controller
                control={control}
                name="fullName"
                render={({ field: { onChange, value, ref, onBlur, name } }) => (
                  <TextField
                    fullWidth
                    type="text"
                    className="comment-field"
                    size={'medium'}
                    disabled={!isEditMode}
                    label={fields.full_name.title}
                    variant="outlined"
                    name={name}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    value={value || ''}
                    error={!!errors.fullName}
                    InputProps={{ classes: { input: 'browser-default' } }}
                  />
                )}
              />
              <Controller
                control={control}
                name="email"
                render={({ field: { onChange, value, ref, onBlur, name } }) => (
                  <TextField
                    fullWidth
                    type="text"
                    className="comment-field"
                    size={'medium'}
                    disabled={!isEditMode}
                    label={fields.email.title}
                    variant="outlined"
                    name={name}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    value={value || ''}
                    error={!!errors.fullName}
                    InputProps={{ classes: { input: 'browser-default' } }}
                  />
                )}
              />
              <Controller
                control={control}
                name="accessRight"
                render={({ field: { onChange, value, ref, onBlur } }) => (
                  <Autocomplete
                    disablePortal
                    disableClearable
                    fullWidth
                    className="access-right"
                    PopperComponent={CustomPopper}
                    options={fields.role.items ?? []}
                    disabled={!isEditMode}
                    onChange={(_, value) => onChange(value ? value.code : null)}
                    onBlur={onBlur}
                    ref={ref}
                    value={
                      typeof value === 'number'
                        ? (fields.role.items ?? []).find((c) => c.code === value) ?? null
                        : null
                    }
                    popupIcon={<KeyboardArrowDownIcon />}
                    classes={{ input: 'browser-default' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={fields.role.title}
                        error={!!errors.accessRight}
                      />
                    )}
                    renderOption={(props, option) => {
                      return (
                        <MenuItem
                          {...props}
                          value={option.code}
                          key={option.code}
                          className="a-service-field-selector-option--root"
                        >
                          {option.title}
                        </MenuItem>
                      );
                    }}
                    getOptionLabel={(option: ItemSelector<number>) => option.title}
                  />
                )}
              />

              <Controller
                control={control}
                name="workingWithApplications"
                render={({ field: { onChange, value, ref, onBlur } }) => (
                  <Autocomplete
                    disablePortal
                    disableClearable
                    fullWidth
                    className="working-with-applications"
                    PopperComponent={CustomPopper}
                    options={fields.work_type.items ?? []}
                    disabled={!isEditMode}
                    onChange={(_, value) => onChange(value ? value.code : null)}
                    onBlur={onBlur}
                    ref={ref}
                    value={
                      typeof value === 'number'
                        ? (fields.work_type.items ?? []).find((c) => c.code === value) ?? null
                        : null
                    }
                    popupIcon={<KeyboardArrowDownIcon />}
                    classes={{ input: 'browser-default' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={fields.work_type.title}
                        error={!!errors.workingWithApplications}
                      />
                    )}
                    renderOption={(props, option) => {
                      return (
                        <MenuItem
                          {...props}
                          value={option.code}
                          key={option.code}
                          className="a-service-field-selector-option--root"
                        >
                          {option.title}
                        </MenuItem>
                      );
                    }}
                    getOptionLabel={(option: ItemSelector<number>) => option.title}
                  />
                )}
              />
              <div className="module-control-wrapper">
                <div className="module-control-wrapper__buttons">
                  {(isNewRepresentative || isEditMode) && (
                    <Tooltip title={representativeData.messages.btn_save}>
                      <IconButton
                        color="primary"
                        size={'small'}
                        type="submit"
                        sx={{ marginRight: '2px' }}
                      >
                        <SaveIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!isNewRepresentative && !isEditMode && (
                    <Tooltip title={representativeData.messages.btn_edit}>
                      <IconButton
                        color="primary"
                        size={'small'}
                        onClick={() => changeEditModeHandler(true)}
                      >
                        <EditIcon fontSize={'small'} sx={{ color: '#3460B4' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isEditMode && !isNewRepresentative && (
                    <Tooltip title={representativeData.messages.btn_undo}>
                      <IconButton
                        color="primary"
                        size={'small'}
                        sx={{ marginRight: '2px' }}
                        onClick={cancelEditingHandler}
                      >
                        <RedoIcon fontSize={'small'} sx={{ color: '#707070' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {(!isEditMode || isNewRepresentative) && (
                    <Tooltip title={representativeData.messages.btn_delete}>
                      <IconButton
                        color="primary"
                        size="small"
                        disabled={totalCount === 1}
                        onClick={() =>
                          onRemoveRepresentativesItem(index, representativesItem.id, (errors) => {
                            if (errors.length >= 1) {
                              setError('accessRight', { message: errors[0] });
                            }
                          })
                        }
                      >
                        <CloseIcon fontSize={'small'} sx={{ color: '#F15642' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
            {!!errors.root && (
              <FormHelperText className="root-error" error>
                {errors.root.message}
              </FormHelperText>
            )}
          </CardWrapper>
        </form>
      </RepresentativesItemStyledContainer>
      <ChangeNotification
        needReviewType={false}
        isOpen={isChangeNotificationOpen}
        onClose={() => setIsChangeNotificationOpen(false)}
      />
    </>
  );
}
export { RepresentativesItem };
